/**
 * Angular imports.
 */
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormControl } from '@angular/forms';
import { DYNAMIC_FORM_IDENTIFIERS, MOBILE_VALIDATION_REGEX } from '../../constants/app.constants';

/**
 * Model imports.
 */
import { ConsentFormValidators, CountryCodes, DynamicFormControl, Label } from '../../onboarding/interface/global.model';

/**
 * Validations imports.
 */
import { NoSpaceValidator } from '../../validators/noSpaceValidator/noSpace.validator';

/**
 * This is dynamic reactive form based on the API.
 */
@Component({
  selector: 'app-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss']
})
export class DynamicFormComponent implements OnInit {

  /**
   * FormData from the api.
   * Labels for the title, accept and reject button.
   * Disclaimer for the form.
   * Country code list.
   */
  @Input() public formData: DynamicFormControl[];
  @Input() public label: Label;
  @Input() public disclaimer: string;
  @Input() public countryCodes: CountryCodes[] = [];
  @Input() public isConsentButtonDisabled = false;

  @Input() public consentErrorValidationMsg = { invalid: '' };

  /**
   * Emit the reset and submit form event outside.
   */
  @Output() public emitReset: EventEmitter<void> = new EventEmitter();
  @Output() public emitSubmit: EventEmitter<object> = new EventEmitter();

  /**
   * Submit form flag and error messages based on validations.
   */
  private submitted = false;
  private ERROR_MESSAGES = {
    pattern: () => `${this.consentErrorValidationMsg.invalid}`,
    required: () => '',
    requiredTrue: () => '',
    cannotContainSpace: () => '',
    minlength: () => '',
    maxlength: () => ''
  };

  /**
   * Empty Dynamic form group.
   * Preselected country code.
   * Identifier for the country code dropdown.
   * Country code multiselect settings.
   */
  public dynamicForm: UntypedFormGroup = this.formBuilder.group({});
  public selectedCode: CountryCodes[] = [];
  public dynamicFormIdentifiers = DYNAMIC_FORM_IDENTIFIERS;
  public codeDropdownSettings = {
    singleSelection: true,
    enableSearchFilter: true,
    showCheckbox: false,
    classes: '',
    enableCheckAll: false,
    enableFilterSelectAll: false,
    disabled: false,
    single_select: true,
    enable_search: true,
    position: 'top',
    autoPosition: false,
    tagToBody: true
  };
  /**
   * Necessary instances.
   */
  constructor(
    private formBuilder: UntypedFormBuilder
  ) { }

  /**
   * Create dynamic form.
   */
  ngOnInit(): void {
    this.codeDropdownSettings.classes = '';
    this.createForm(this.formData);
  }

  /**
   * Add the validations and form controls.
   */
  createForm(controls: DynamicFormControl[]): void {
    for (const control of controls) {
      const validatorsToAdd = [];
      if (control.validations.required) {
        validatorsToAdd.push(Validators.required);
      }
      if (control.validations.minLength) {
        validatorsToAdd.push(Validators.minLength(control.validations.minLength));
      }
      if (control.validations.maxLength) {
        validatorsToAdd.push(Validators.maxLength(control.validations.maxLength));
      }
      if (control.validations.pattern) {
        validatorsToAdd.push(Validators.pattern(control.validations.pattern.slice(1, -1)));
      }
      validatorsToAdd.push(NoSpaceValidator);

      if (control.name === this.dynamicFormIdentifiers.CODE) {
        this.selectedCode.push({ id: 0, code: control.value.code || this.countryCodes[0].code, name: '' });
        this.codeDropdownSettings.disabled = !control.validations.editable;
      }
      this.dynamicForm.addControl(
        control.name,
        this.formBuilder.control({ value: control.value.text, disabled: !control.validations.editable }, validatorsToAdd)
      );
    }
  }

  /**
   * Condition for when to show the error messages.
   */
  shouldShowErrors(control: UntypedFormControl): boolean {
    return control && (control.errors && control.touched);
  }

  /**
   * List of errors based on the keys.
   */
  listOfError(control: UntypedFormControl, label: string): string[] {
    return Object.keys(control.errors).map((err) => this.ERROR_MESSAGES[err](control.getError(err), label));
  }

  /**
   * Validation for the mobile number.
   */
  mobileNumberValidation(event, validation: ConsentFormValidators): void {
    if (!this.isMobileNumberValid(event) || (validation.maxLength && event.target.value.length === validation.maxLength)) {
      event.preventDefault();
    }
  }

  /**
   * Check the validation fot the mobile number.
   */
  isMobileNumberValid(event: KeyboardEvent): boolean {
    const regex = new RegExp(MOBILE_VALIDATION_REGEX);
    return regex.test(event.key);
  }

  /**
   * Get the Selected country and assign to selected code.
   */
  getSelectedCountry(value: CountryCodes): void {
    this.selectedCode = [value];
  }

  /**
   * Check the validations, Submit the form and output the values outside.
   */
  onSubmit(): void {
    this.submitted = true;
    if (this.dynamicForm.invalid) {
      return;
    }

    this.emitSubmit.emit({ formValue: this.dynamicForm.getRawValue(), code: this.selectedCode });
  }

  /**
   * Reset the form and output the event outside.
   */
  onReset(): void {
    this.submitted = false;
    this.emitReset.emit();
  }
}
